import { type Column, type RangeSpec, TemplateDoc, type TemplateDocInput } from './template';

/**
 * Starter templates, transcribed from the sample reports in docs/samples.
 * Reference ranges are the samples' own and must be confirmed by each lab for its analyzers.
 * Ids are stable strings so starters can be updated in place.
 */

const cols = (prefix: string, split = false): Column[] =>
  split
    ? [
        { id: `${prefix}.c.inv`, role: 'investigation', label: 'Investigation' },
        { id: `${prefix}.c.res`, role: 'result', label: 'Result' },
        { id: `${prefix}.c.min`, role: 'ref_min', label: 'Reference Value Min' },
        { id: `${prefix}.c.max`, role: 'ref_max', label: 'Reference Value Max' },
        { id: `${prefix}.c.unit`, role: 'unit', label: 'Unit' },
      ]
    : [
        { id: `${prefix}.c.inv`, role: 'investigation', label: 'Test Name' },
        { id: `${prefix}.c.res`, role: 'result', label: 'Result' },
        { id: `${prefix}.c.unit`, role: 'unit', label: 'Unit' },
        { id: `${prefix}.c.ref`, role: 'ref_range', label: 'Bio. Ref. Interval' },
      ];

const between = (min: number, max: number, extra: Partial<RangeSpec> = {}): RangeSpec =>
  ({ type: 'between', min, max, ...extra }) as RangeSpec;

type T = {
  code: string;
  label: string;
  unit?: string;
  decimals?: number;
  method?: string;
  formula?: string;
  ranges?: RangeSpec[];
  valueType?: 'numeric' | 'text' | 'option';
  options?: string[];
  required?: boolean;
  critical?: { low?: number; high?: number };
  loinc?: string;
};

const test = (prefix: string, t: T) => ({
  id: `${prefix}.${t.code.toLowerCase()}`,
  kind: 'test' as const,
  valueType: t.valueType ?? ('numeric' as const),
  required: t.required ?? false,
  ranges: t.ranges ?? [],
  ...t,
});
const section = (prefix: string, key: string, label: string) => ({
  id: `${prefix}.s.${key}`,
  kind: 'section' as const,
  label,
});

const P_CBC = 'cbc';
const cbc: TemplateDocInput = {
  name: 'Complete Blood Count (CBC)',
  aliases: ['CBC', 'Hemogram', 'Haemogram', 'Complete Hemogram'],
  department: 'Haematology',
  sampleType: 'Whole Blood EDTA',
  columns: cols(P_CBC),
  rows: [
    test(P_CBC, {
      code: 'HB',
      label: 'Haemoglobin (Hb)',
      unit: 'g/dL',
      decimals: 1,
      method: 'Colorimetric',
      loinc: '718-7',
      required: true,
      critical: { low: 7, high: 20 },
      ranges: [between(13, 17, { sex: 'M' }), between(12, 15, { sex: 'F' })],
    }),
    test(P_CBC, {
      code: 'RBC',
      label: 'RBC Count',
      unit: 'million/µL',
      decimals: 2,
      method: 'Electrical impedance',
      loinc: '789-8',
      required: true,
      ranges: [between(4.5, 5.5, { sex: 'M' }), between(3.8, 4.8, { sex: 'F' })],
    }),
    test(P_CBC, {
      code: 'PCV',
      label: 'Packed Cell Volume (PCV)',
      unit: '%',
      decimals: 1,
      loinc: '4544-3',
      required: true,
      ranges: [between(40, 50, { sex: 'M' }), between(36, 46, { sex: 'F' })],
    }),
    test(P_CBC, {
      code: 'MCV',
      label: 'MCV',
      unit: 'fL',
      decimals: 1,
      formula: 'PCV * 10 / RBC',
      loinc: '787-2',
      ranges: [between(83, 101)],
    }),
    test(P_CBC, {
      code: 'MCH',
      label: 'MCH',
      unit: 'pg',
      decimals: 1,
      formula: 'HB * 10 / RBC',
      loinc: '785-6',
      ranges: [between(27, 32)],
    }),
    test(P_CBC, {
      code: 'MCHC',
      label: 'MCHC',
      unit: 'g/dL',
      decimals: 1,
      formula: 'HB * 100 / PCV',
      loinc: '786-4',
      ranges: [between(31.5, 34.5)],
    }),
    test(P_CBC, {
      code: 'RDWCV',
      label: 'RDW-CV',
      unit: '%',
      decimals: 1,
      ranges: [between(11.6, 14)],
    }),
    test(P_CBC, {
      code: 'PLT',
      label: 'Platelet Count',
      unit: '10³/µL',
      decimals: 0,
      method: 'Electrical impedance/Microscopy',
      loinc: '777-3',
      critical: { low: 20, high: 1000 },
      ranges: [between(150, 410)],
    }),
    test(P_CBC, {
      code: 'TLC',
      label: 'Total Leukocyte Count (TLC)',
      unit: '10³/µL',
      decimals: 2,
      method: 'Electrical impedance',
      loinc: '6690-2',
      required: true,
      critical: { low: 2, high: 30 },
      ranges: [between(4, 10)],
    }),
    section(P_CBC, 'dlc', 'DIFFERENTIAL LEUCOCYTE COUNT'),
    test(P_CBC, {
      code: 'NEUT',
      label: 'Neutrophils',
      unit: '%',
      decimals: 1,
      ranges: [between(40, 80)],
    }),
    test(P_CBC, {
      code: 'LYMPH',
      label: 'Lymphocytes',
      unit: '%',
      decimals: 1,
      ranges: [between(20, 40)],
    }),
    test(P_CBC, {
      code: 'EOS',
      label: 'Eosinophils',
      unit: '%',
      decimals: 1,
      ranges: [between(1, 6)],
    }),
    test(P_CBC, {
      code: 'MONO',
      label: 'Monocytes',
      unit: '%',
      decimals: 1,
      ranges: [between(2, 10)],
    }),
    test(P_CBC, {
      code: 'BASO',
      label: 'Basophils',
      unit: '%',
      decimals: 1,
      ranges: [between(0, 1)],
    }),
    section(P_CBC, 'abs', 'ABSOLUTE COUNTS'),
    test(P_CBC, {
      code: 'ANC',
      label: 'Absolute Neutrophil Count',
      unit: '10³/µL',
      decimals: 2,
      formula: 'TLC * NEUT / 100',
      ranges: [between(2, 7)],
    }),
    test(P_CBC, {
      code: 'ALC',
      label: 'Absolute Lymphocyte Count',
      unit: '10³/µL',
      decimals: 2,
      formula: 'TLC * LYMPH / 100',
      ranges: [between(1, 3)],
    }),
    test(P_CBC, {
      code: 'AEC',
      label: 'Absolute Eosinophil Count',
      unit: '10³/µL',
      decimals: 2,
      formula: 'TLC * EOS / 100',
      ranges: [between(0.02, 0.5)],
    }),
    test(P_CBC, {
      code: 'AMC',
      label: 'Absolute Monocyte Count',
      unit: '10³/µL',
      decimals: 2,
      formula: 'TLC * MONO / 100',
      ranges: [between(0.2, 1)],
    }),
    test(P_CBC, {
      code: 'ABC',
      label: 'Absolute Basophil Count',
      unit: '10³/µL',
      decimals: 2,
      formula: 'TLC * BASO / 100',
      ranges: [between(0.02, 0.1)],
    }),
  ],
};

const P_LFT = 'lft';
const lft: TemplateDocInput = {
  name: 'Liver Function Test (LFT)',
  aliases: ['LFT', 'Liver Panel', 'Liver Profile'],
  department: 'Biochemistry',
  sampleType: 'Serum',
  columns: cols(P_LFT),
  rows: [
    test(P_LFT, {
      code: 'TBIL',
      label: 'Bilirubin Total',
      unit: 'mg/dL',
      decimals: 2,
      method: 'Diazonium ion',
      ranges: [{ type: 'lte', max: 1.2 }],
    }),
    test(P_LFT, {
      code: 'DBIL',
      label: 'Bilirubin Direct',
      unit: 'mg/dL',
      decimals: 2,
      ranges: [{ type: 'lte', max: 0.2 }],
    }),
    test(P_LFT, {
      code: 'IBIL',
      label: 'Bilirubin Indirect',
      unit: 'mg/dL',
      decimals: 2,
      formula: 'TBIL - DBIL',
      ranges: [{ type: 'lte', max: 1.1 }],
    }),
    test(P_LFT, {
      code: 'ALT',
      label: 'SGPT (ALT)',
      unit: 'U/L',
      decimals: 1,
      ranges: [{ type: 'lte', max: 35 }],
    }),
    test(P_LFT, {
      code: 'AST',
      label: 'SGOT (AST)',
      unit: 'U/L',
      decimals: 1,
      ranges: [{ type: 'lte', max: 40 }],
    }),
    test(P_LFT, {
      code: 'ALP',
      label: 'Alkaline Phosphatase',
      unit: 'U/L',
      decimals: 1,
      method: 'IFCC',
      ranges: [between(53, 128)],
    }),
    test(P_LFT, {
      code: 'GGT',
      label: 'Gamma GT (GGT)',
      unit: 'U/L',
      decimals: 1,
      method: 'IFCC',
      ranges: [between(12, 58)],
    }),
    test(P_LFT, {
      code: 'TP',
      label: 'Total Protein',
      unit: 'g/dL',
      decimals: 2,
      method: 'Biuret',
      ranges: [between(6, 8.3)],
    }),
    test(P_LFT, {
      code: 'ALB',
      label: 'Albumin',
      unit: 'g/dL',
      decimals: 2,
      method: 'BCG',
      ranges: [between(3.2, 5.2)],
    }),
    test(P_LFT, {
      code: 'GLOB',
      label: 'Globulin',
      unit: 'g/dL',
      decimals: 2,
      formula: 'TP - ALB',
      ranges: [between(2.3, 4.5)],
    }),
    test(P_LFT, {
      code: 'AGR',
      label: 'A/G Ratio',
      decimals: 2,
      formula: 'ALB / (TP - ALB)',
      ranges: [between(1, 2.3)],
    }),
    test(P_LFT, { code: 'ASTALT', label: 'SGOT/SGPT Ratio', decimals: 2, formula: 'AST / ALT' }),
  ],
};

const P_KFT = 'kft';
const kft: TemplateDocInput = {
  name: 'Kidney Function Test (KFT)',
  aliases: ['KFT', 'RFT', 'Renal Function Test', 'Renal Profile'],
  department: 'Biochemistry',
  sampleType: 'Serum',
  columns: cols(P_KFT),
  rows: [
    test(P_KFT, {
      code: 'UREA',
      label: 'Urea',
      unit: 'mg/dL',
      decimals: 2,
      method: 'Urease UV',
      ranges: [between(19, 44)],
    }),
    test(P_KFT, {
      code: 'CREAT',
      label: 'Creatinine',
      unit: 'mg/dL',
      decimals: 2,
      method: 'Jaffe kinetic',
      critical: { high: 7 },
      ranges: [between(0.6, 1.3)],
    }),
    test(P_KFT, {
      code: 'URIC',
      label: 'Uric Acid',
      unit: 'mg/dL',
      decimals: 2,
      ranges: [between(3.5, 7.2)],
    }),
    test(P_KFT, {
      code: 'NA',
      label: 'Sodium',
      unit: 'mmol/L',
      decimals: 1,
      method: 'ISE',
      critical: { low: 120, high: 160 },
      ranges: [between(135, 150)],
    }),
    test(P_KFT, {
      code: 'K',
      label: 'Potassium',
      unit: 'mmol/L',
      decimals: 2,
      method: 'ISE',
      critical: { low: 2.5, high: 6.5 },
      ranges: [between(3.5, 5.5)],
    }),
    test(P_KFT, {
      code: 'CL',
      label: 'Chloride',
      unit: 'mmol/L',
      decimals: 1,
      method: 'ISE',
      ranges: [between(94, 110)],
    }),
    test(P_KFT, {
      code: 'BUN',
      label: 'Blood Urea Nitrogen (BUN)',
      unit: 'mg/dL',
      decimals: 2,
      formula: 'UREA * 0.467',
      ranges: [between(8, 23)],
    }),
    test(P_KFT, {
      code: 'BUNCR',
      label: 'BUN/Creatinine Ratio',
      decimals: 2,
      formula: 'BUN / CREAT',
      ranges: [between(10, 20)],
    }),
    test(P_KFT, {
      code: 'UREACR',
      label: 'Urea/Creatinine Ratio',
      decimals: 2,
      formula: 'UREA / CREAT',
      ranges: [between(40, 100)],
    }),
  ],
};

const P_LIPID = 'lipid';
const lipid: TemplateDocInput = {
  name: 'Lipid Profile',
  aliases: ['Lipid', 'Lipid Panel', 'Cholesterol Test'],
  department: 'Biochemistry',
  sampleType: 'Serum',
  columns: cols(P_LIPID),
  rows: [
    test(P_LIPID, {
      code: 'TC',
      label: 'Cholesterol Total',
      unit: 'mg/dL',
      decimals: 2,
      ranges: [
        {
          type: 'bands',
          bands: [
            { label: 'Desirable', max: 200, flag: 'normal' },
            { label: 'Borderline high', min: 200, max: 240, flag: 'high' },
            { label: 'High', min: 240, flag: 'high' },
          ],
        },
      ],
    }),
    test(P_LIPID, {
      code: 'TG',
      label: 'Triglycerides',
      unit: 'mg/dL',
      decimals: 2,
      ranges: [{ type: 'lt', max: 150 }],
    }),
    test(P_LIPID, {
      code: 'HDL',
      label: 'HDL Cholesterol',
      unit: 'mg/dL',
      decimals: 2,
      ranges: [{ type: 'gt', min: 40 }],
    }),
    test(P_LIPID, {
      code: 'NHDL',
      label: 'Non-HDL Cholesterol',
      unit: 'mg/dL',
      decimals: 2,
      formula: 'TC - HDL',
      ranges: [{ type: 'lt', max: 130 }],
    }),
    test(P_LIPID, {
      code: 'VLDL',
      label: 'VLDL Cholesterol',
      unit: 'mg/dL',
      decimals: 2,
      formula: 'TG / 5',
      ranges: [between(0, 45)],
    }),
    test(P_LIPID, {
      code: 'LDL',
      label: 'LDL Cholesterol (calculated)',
      unit: 'mg/dL',
      decimals: 2,
      formula: 'TC - HDL - TG / 5',
      ranges: [{ type: 'lt', max: 130 }],
    }),
    test(P_LIPID, {
      code: 'TCHDL',
      label: 'Chol/HDL Ratio',
      decimals: 2,
      formula: 'TC / HDL',
      ranges: [{ type: 'lt', max: 5 }],
    }),
    test(P_LIPID, {
      code: 'LDLHDL',
      label: 'LDL/HDL Ratio',
      decimals: 2,
      formula: 'LDL / HDL',
      ranges: [between(1, 3.5)],
    }),
  ],
};

const P_A1C = 'hba1c';
const hba1c: TemplateDocInput = {
  name: 'HbA1c (Glycosylated Haemoglobin)',
  aliases: ['HbA1c', 'A1c', 'Glycated Hemoglobin'],
  department: 'Haematology',
  sampleType: 'Whole Blood EDTA',
  columns: cols(P_A1C),
  rows: [
    test(P_A1C, {
      code: 'A1C',
      label: 'HbA1c',
      unit: '%',
      decimals: 2,
      method: 'HPLC',
      loinc: '4548-4',
      ranges: [
        {
          type: 'bands',
          bands: [
            { label: 'Normal', max: 5.7, flag: 'normal' },
            { label: 'Prediabetes', min: 5.7, max: 6.5, flag: 'high' },
            { label: 'Diabetes', min: 6.5, flag: 'high' },
          ],
        },
      ],
    }),
    test(P_A1C, {
      code: 'EAG',
      label: 'Estimated Average Glucose',
      unit: 'mg/dL',
      decimals: 2,
      formula: '28.7 * A1C - 46.7',
      ranges: [between(68, 125)],
    }),
  ],
};

const P_VITD = 'vitd';
const vitaminD: TemplateDocInput = {
  name: 'Vitamin D (25-OH)',
  aliases: ['Vitamin D', 'Vit D', '25 OH Vitamin D'],
  department: 'Immunology',
  sampleType: 'Serum',
  columns: cols(P_VITD),
  rows: [
    test(P_VITD, {
      code: 'VITD',
      label: 'Vitamin D (25-OH)',
      unit: 'ng/mL',
      decimals: 2,
      method: 'ECLIA',
      ranges: [
        {
          type: 'bands',
          bands: [
            { label: 'Deficiency', max: 20, flag: 'low' },
            { label: 'Insufficiency', min: 20, max: 30, flag: 'low' },
            { label: 'Sufficiency', min: 30, max: 100, maxInclusive: true, flag: 'normal' },
            { label: 'Toxicity', min: 100, minExclusive: true, flag: 'high' },
          ],
        },
      ],
    }),
  ],
};

const P_IRON = 'iron';
const iron: TemplateDocInput = {
  name: 'Iron Profile',
  aliases: ['Iron Studies', 'Serum Iron', 'TIBC'],
  department: 'Biochemistry',
  sampleType: 'Serum',
  columns: cols(P_IRON),
  rows: [
    test(P_IRON, {
      code: 'FE',
      label: 'Iron',
      unit: 'µg/dL',
      decimals: 2,
      ranges: [between(59, 158)],
    }),
    test(P_IRON, {
      code: 'UIBC',
      label: 'UIBC',
      unit: 'µg/dL',
      decimals: 2,
      ranges: [between(110, 370)],
    }),
    test(P_IRON, {
      code: 'TIBC',
      label: 'Total Iron Binding Capacity',
      unit: 'µg/dL',
      decimals: 2,
      formula: 'FE + UIBC',
      ranges: [between(240, 450)],
    }),
    test(P_IRON, {
      code: 'TSAT',
      label: 'Transferrin Saturation',
      unit: '%',
      decimals: 2,
      formula: 'FE / (FE + UIBC) * 100',
      ranges: [between(20, 50)],
    }),
  ],
};

const P_TFT = 'tft';
const thyroid: TemplateDocInput = {
  name: 'Thyroid Profile (T3, T4, TSH)',
  aliases: ['Thyroid', 'TFT', 'T3 T4 TSH'],
  department: 'Immunology',
  sampleType: 'Serum',
  columns: cols(P_TFT),
  rows: [
    test(P_TFT, {
      code: 'T3',
      label: 'Triiodothyronine Total (T3)',
      unit: 'ng/mL',
      decimals: 2,
      method: 'ECLIA',
      ranges: [between(0.7, 2.04)],
    }),
    test(P_TFT, {
      code: 'T4',
      label: 'Thyroxine Total (T4)',
      unit: 'µg/dL',
      decimals: 2,
      method: 'ECLIA',
      ranges: [between(4.6, 10.5)],
    }),
    test(P_TFT, {
      code: 'TSH',
      label: 'TSH',
      unit: 'µIU/mL',
      decimals: 3,
      method: 'ECLIA',
      ranges: [between(0.35, 5.5)],
    }),
  ],
};

const P_URINE = 'urine';
const urine: TemplateDocInput = {
  name: 'Urine Routine Examination',
  aliases: ['Urine R/M', 'Urine Routine', 'Urinalysis'],
  department: 'Clinical Pathology',
  sampleType: 'Urine',
  columns: cols(P_URINE),
  rows: [
    section(P_URINE, 'phy', 'PHYSICAL EXAMINATION'),
    test(P_URINE, { code: 'VOL', label: 'Volume', unit: 'mL', valueType: 'numeric' }),
    test(P_URINE, {
      code: 'COLOUR',
      label: 'Colour',
      valueType: 'option',
      options: ['Pale Yellow', 'Yellow', 'Dark Yellow', 'Amber', 'Red'],
      ranges: [{ type: 'text', display: 'Pale yellow to amber' }],
    }),
    test(P_URINE, {
      code: 'APPEAR',
      label: 'Appearance',
      valueType: 'option',
      options: ['Clear', 'Slightly turbid', 'Turbid'],
      ranges: [{ type: 'qualitative', expected: ['Clear'] }],
    }),
    test(P_URINE, { code: 'PH', label: 'pH', decimals: 1, ranges: [between(4.6, 8)] }),
    test(P_URINE, {
      code: 'SG',
      label: 'Specific Gravity',
      decimals: 3,
      ranges: [between(1.005, 1.03)],
    }),
    section(P_URINE, 'chem', 'CHEMICAL EXAMINATION'),
    test(P_URINE, {
      code: 'UPROT',
      label: 'Protein',
      valueType: 'option',
      options: ['Nil', 'Trace', '+', '++', '+++'],
      ranges: [{ type: 'qualitative', expected: ['Negative'] }],
    }),
    test(P_URINE, {
      code: 'UGLU',
      label: 'Glucose',
      valueType: 'option',
      options: ['Nil', 'Trace', '+', '++', '+++'],
      ranges: [{ type: 'qualitative', expected: ['Negative'] }],
    }),
    test(P_URINE, {
      code: 'KET',
      label: 'Ketones',
      valueType: 'option',
      options: ['Nil', 'Trace', '+', '++'],
      ranges: [{ type: 'qualitative', expected: ['Negative'] }],
    }),
    section(P_URINE, 'micro', 'MICROSCOPIC EXAMINATION'),
    test(P_URINE, {
      code: 'PUS',
      label: 'Pus Cells',
      unit: '/hpf',
      valueType: 'text',
      ranges: [between(0, 3)],
    }),
    test(P_URINE, {
      code: 'URBC',
      label: 'RBCs',
      unit: '/hpf',
      valueType: 'text',
      ranges: [{ type: 'qualitative', expected: ['Nil'] }],
    }),
    test(P_URINE, {
      code: 'EPI',
      label: 'Epithelial Cells',
      unit: '/hpf',
      valueType: 'text',
      ranges: [between(1, 2)],
    }),
    test(P_URINE, {
      code: 'CRYST',
      label: 'Crystals',
      valueType: 'text',
      ranges: [{ type: 'qualitative', expected: ['Nil'] }],
    }),
    test(P_URINE, {
      code: 'BACT',
      label: 'Bacteria',
      valueType: 'text',
      ranges: [{ type: 'qualitative', expected: ['Nil'] }],
    }),
    {
      id: `${P_URINE}.n.culture`,
      kind: 'note',
      text: 'Urine culture and sensitivity is advised if pus cells are 10 or more with nitrite positive.',
    },
  ],
};

const P_SEMEN = 'semen';
const semen: TemplateDocInput = {
  name: 'Semen Analysis',
  aliases: ['Semen', 'Seminal Fluid Analysis', 'Sperm Count'],
  department: 'Clinical Pathology',
  sampleType: 'Semen',
  sensitive: true,
  columns: cols(P_SEMEN),
  extraFields: [
    { key: 'collection_time', label: 'Time of collection', type: 'datetime' },
    { key: 'arrival_time', label: 'Time arrived in lab', type: 'datetime' },
    { key: 'abstinence_days', label: 'Sexual abstinence (days)', type: 'number' },
  ],
  rows: [
    test(P_SEMEN, {
      code: 'SVOL',
      label: 'Volume',
      unit: 'mL',
      decimals: 1,
      ranges: [between(1.5, 5)],
    }),
    test(P_SEMEN, {
      code: 'LIQ',
      label: 'Liquefaction',
      unit: 'min',
      decimals: 0,
      ranges: [{ type: 'lte', max: 20, display: 'within 20 minutes' }],
    }),
    test(P_SEMEN, { code: 'SPH', label: 'pH', decimals: 1, ranges: [between(7, 8)] }),
    test(P_SEMEN, {
      code: 'COUNT',
      label: 'Total sperm count',
      unit: 'million/mL',
      valueType: 'text',
      ranges: [between(60, 150)],
    }),
    test(P_SEMEN, {
      code: 'ACTIVE',
      label: 'Actively motile',
      unit: '%',
      valueType: 'text',
      ranges: [{ type: 'gt', min: 60, display: 'More than 60 %' }],
    }),
    test(P_SEMEN, {
      code: 'NONMOT',
      label: 'Non-motile',
      unit: '%',
      valueType: 'text',
      ranges: [{ type: 'lt', max: 20, display: 'Less than 20 %' }],
    }),
  ],
};

// ---- Added from the sample reports of a Srinagar reference laboratory (CLIA analyser) -------

const PREG_TSH = [
  'For pregnant females, bio reference range for TSH in mIU/L (as per the American Thyroid Association):',
  'First trimester 0.10 - 2.50',
  'Second trimester 0.20 - 3.00',
  'Third trimester 0.30 - 3.50',
].join('\n');

const P_TSHU = 'tshu';
const tshUltra: TemplateDocInput = {
  name: 'TSH, Ultrasensitive',
  aliases: ['TSH', 'Ultrasensitive TSH', 'TSH Ultra', 'Thyroid Stimulating Hormone'],
  department: 'Immunology',
  sampleType: 'Serum',
  columns: cols(P_TSHU),
  rows: [
    test(P_TSHU, {
      code: 'TSH',
      label: 'Thyroid Stimulating Hormone (Ultrasensitive)',
      unit: 'mIU/L',
      decimals: 2,
      method: 'Chemiluminescence (CLIA)',
      required: true,
      ranges: [between(0.55, 4.78)],
    }),
    { id: `${P_TSHU}.n.preg`, kind: 'note' as const, text: PREG_TSH },
    {
      id: `${P_TSHU}.n.circ`,
      kind: 'note' as const,
      text: 'TSH levels vary through the day, with a peak between 2 and 4 am and a low between 6 and 10 pm. Values below 0.03 mIU/L need clinical correlation because of a rare TSH variant in some people.',
    },
  ],
};

const P_TFTU = 'tftu';
const thyroidUltra: TemplateDocInput = {
  name: 'Thyroid Profile with Ultrasensitive TSH',
  aliases: ['Thyroid Profile', 'TFT', 'T3 T4 TSH', 'Thyroid Profile Ultra'],
  department: 'Immunology',
  sampleType: 'Serum',
  columns: cols(P_TFTU),
  rows: [
    test(P_TFTU, {
      code: 'T3',
      label: 'Tri-iodothyronine (T3)',
      unit: 'ng/dL',
      decimals: 1,
      method: 'Chemiluminescence (CLIA)',
      ranges: [between(60, 181)],
    }),
    test(P_TFTU, {
      code: 'T4',
      label: 'Thyroxine (T4)',
      unit: 'µg/dL',
      decimals: 2,
      method: 'Chemiluminescence (CLIA)',
      ranges: [between(3.2, 12.6)],
    }),
    test(P_TFTU, {
      code: 'TSH',
      label: 'Thyroid Stimulating Hormone (Ultrasensitive)',
      unit: 'mIU/L',
      decimals: 2,
      method: 'Chemiluminescence (CLIA)',
      ranges: [between(0.55, 4.78)],
    }),
    { id: `${P_TFTU}.n.preg`, kind: 'note' as const, text: PREG_TSH },
    {
      id: `${P_TFTU}.n.circ`,
      kind: 'note' as const,
      text: 'TSH levels vary through the day, with a peak between 2 and 4 am and a low between 6 and 10 pm. Changes in thyroid hormone binding proteins, in pregnancy and with steroid therapy, can affect total T3 and T4.',
    },
  ],
};

const P_MPV = 'mpv';
const mpv: TemplateDocInput = {
  name: 'Mean Platelet Volume (MPV)',
  aliases: ['MPV'],
  department: 'Haematology',
  sampleType: 'Whole Blood EDTA',
  columns: cols(P_MPV),
  rows: [
    test(P_MPV, {
      code: 'MPV',
      label: 'Mean Platelet Volume (MPV)',
      unit: 'fL',
      decimals: 1,
      method: 'Impedance',
      required: true,
      ranges: [between(8, 11)],
    }),
  ],
};

// ---- Coagulation, from a clinical laboratory's printed reports -------------------------------

const note = (prefix: string, key: string, text: string) => ({
  id: `${prefix}.n.${key}`,
  kind: 'note' as const,
  text,
});
const narrative = (prefix: string, key: string, text: string) => ({
  id: `${prefix}.t.${key}`,
  kind: 'narrative' as const,
  text,
});

const P_PT = 'pt';
const ptInr: TemplateDocInput = {
  name: 'Prothrombin Time (PT / INR)',
  aliases: ['PT', 'PT INR', 'INR', 'Prothrombin Time', 'Pro-time', 'PT/INR'],
  department: 'Haematology',
  sampleType: 'Citrated plasma',
  columns: cols(P_PT),
  rows: [
    section(P_PT, 'method', 'PROTHROMBIN TIME STUDIES (Electromechanical Clot Detection)'),
    test(P_PT, {
      code: 'PTC',
      label: 'Mean Normal Prothrombin Time (PT)',
      unit: 'sec',
      decimals: 1,
      method: 'Electromechanical clot detection',
    }),
    test(P_PT, {
      code: 'PT',
      label: 'Patient value',
      unit: 'sec',
      decimals: 1,
      method: 'Electromechanical clot detection',
      required: true,
    }),
    test(P_PT, {
      code: 'INR',
      label: 'International Normalized Ratio (INR)',
      decimals: 2,
      required: true,
      ranges: [between(0.9, 1.3)],
    }),
    section(P_PT, 'note', 'Note'),
    note(
      P_PT,
      'n1',
      '1. INR is the parameter of choice in monitoring adequacy of oral anticoagulant therapy. Appropriate therapeutic range varies with disease and intensity.',
    ),
    note(
      P_PT,
      'n2',
      '2. Prolonged INR suggests potential bleeding disorder / bleeding complications.',
    ),
    note(P_PT, 'n3', '3. Results should be clinically correlated.'),
    note(P_PT, 'n4', '4. Test conducted on citrated plasma.'),
    section(P_PT, 'range', 'Recommended therapeutic range for oral anticoagulant therapy'),
    narrative(
      P_PT,
      'r1',
      [
        'INR 2.0 - 3.0:',
        '• Treatment of venous thrombosis and pulmonary embolism',
        '• Prophylaxis of venous thrombosis (high risk surgery)',
        '• Prevention of systemic embolism in tissue heart valves, AMI, valvular heart disease and atrial fibrillation',
        '• Bileaflet mechanical valve in aortic position',
      ].join('\n'),
    ),
    narrative(
      P_PT,
      'r2',
      ['INR 2.5 - 3.5:', '• Mechanical prosthetic valves', '• Systemic recurrent emboli'].join(
        '\n',
      ),
    ),
    section(P_PT, 'comments', 'Comments'),
    narrative(
      P_PT,
      'c1',
      'Prothrombin time measures the extrinsic coagulation pathway, which consists of activated Factor VII (VIIa), tissue factor and the proteins of the common pathway (Factors X, V, II and fibrinogen). This assay is used to control long term oral anticoagulant therapy, for evaluation of liver function and to evaluate coagulation disorders, especially factors involved in the extrinsic pathway such as Factors V, VII, X, prothrombin and fibrinogen.',
    ),
  ],
};

const P_APTT = 'aptt';
const aptt: TemplateDocInput = {
  name: 'APTT (Activated Partial Thromboplastin Time)',
  aliases: ['APTT', 'aPTT', 'PTT', 'Partial Thromboplastin Time', 'PTTK'],
  department: 'Haematology',
  sampleType: 'Citrated plasma',
  columns: cols(P_APTT),
  rows: [
    section(
      P_APTT,
      'method',
      'PARTIAL THROMBOPLASTIN TIME, ACTIVATED; APTT (Electromechanical Clot Detection)',
    ),
    test(P_APTT, {
      code: 'APTT',
      label: 'Patient value',
      unit: 'sec',
      decimals: 2,
      method: 'Electromechanical clot detection',
      required: true,
      ranges: [between(26.92, 42.41)],
    }),
    section(P_APTT, 'note', 'Note'),
    note(
      P_APTT,
      'n1',
      '1. Degree of prolongation of PTT / APTT is neither predictive of bleeding risk nor of the underlying diagnosis.',
    ),
    note(P_APTT, 'n2', '2. Results should be clinically correlated.'),
    note(P_APTT, 'n3', '3. Test conducted on citrated plasma.'),
    note(
      P_APTT,
      'n4',
      '4. Heparin therapeutic range is not established; for heparin monitoring Anti-Xa is recommended.',
    ),
    section(P_APTT, 'comments', 'Comments'),
    narrative(
      P_APTT,
      'c1',
      'Partial thromboplastin time (PTT / APTT) measures the proteins of the intrinsic coagulation pathway, which consists of Factor XII, prekallikrein, high molecular weight kininogen and Factors VIII, IX and XI. It also measures proteins of the common pathway, namely Factors II, V, X and fibrinogen. PTT is prolonged when Factor VIII level is below 35-40% of normal and Factor XII and high molecular weight kininogen are below 10-15% of normal.',
    ),
    section(P_APTT, 'abn', 'Abnormal Partial Thromboplastin Time'),
    narrative(
      P_APTT,
      'a1',
      [
        '• Associated with bleeding: defects of Factors VIII, IX and XI',
        '• Not associated with bleeding: defects of Factor XII, prekallikrein, high molecular weight kininogen and lupus anticoagulants',
      ].join('\n'),
    ),
    section(P_APTT, 'causes', 'Causes of prolonged PTT / APTT'),
    narrative(
      P_APTT,
      'k1',
      [
        '• Liver disease',
        '• Consumptive coagulopathy',
        '• Circulating anticoagulants including lupus anticoagulant',
        '• Oral anticoagulant therapy',
        '• Factor deficiencies',
      ].join('\n'),
    ),
  ],
};

// ---- Single tests and serology, from the printed reports of a Kashmir clinical laboratory ----
// Ranges are the ones printed on that laboratory's reports. Each lab should confirm them for its
// own analyser before relying on them.

/** Age in days, for the age windows on a range. A year is counted as 365 days. */
const Y = (n: number) => Math.round(n * 365);

/** A one-row template, so a lab can order the test on its own. */
const single = (
  prefix: string,
  t: {
    name: string;
    aliases: string[];
    department: string;
    sampleType: string;
    test: T;
    rows?: ReturnType<typeof note | typeof narrative | typeof section>[];
  },
): TemplateDocInput => ({
  name: t.name,
  aliases: t.aliases,
  department: t.department,
  sampleType: t.sampleType,
  columns: cols(prefix),
  rows: [test(prefix, { required: true, ...t.test }), ...(t.rows ?? [])],
});

/** A rapid test whose result is Negative or Positive. */
const posNeg = (code: string, label: string, method: string): T => ({
  code,
  label,
  method,
  valueType: 'option',
  options: ['Negative', 'Positive'],
  ranges: [{ type: 'qualitative', expected: ['Negative'] }],
});

const calcium = single('ca', {
  name: 'Calcium (Serum)',
  aliases: ['Calcium', 'Serum Calcium', 'Ca'],
  department: 'Biochemistry',
  sampleType: 'Serum',
  test: {
    code: 'CA',
    label: 'Calcium',
    unit: 'mg/dL',
    decimals: 1,
    ranges: [
      between(7.6, 10.7, { ageMaxDays: 28 }),
      between(8.8, 10.8, { ageMinDays: 29, ageMaxDays: Y(18) - 1 }),
      between(8.6, 10.9, { ageMinDays: Y(18) }),
    ],
  },
});

const phosphorus = single('phos', {
  name: 'Phosphorus (Serum)',
  aliases: ['Phosphorus', 'Serum Phosphorus', 'Phosphate', 'PO4', 'Inorganic Phosphorus'],
  department: 'Biochemistry',
  sampleType: 'Serum',
  test: {
    code: 'PHOS',
    label: 'Serum Phosphorus',
    unit: 'mg/dL',
    decimals: 1,
    ranges: [between(4, 7, { ageMaxDays: Y(18) - 1 }), between(2.5, 5, { ageMinDays: Y(18) })],
  },
});

const crp = single('crp', {
  name: 'C-Reactive Protein (CRP)',
  aliases: ['CRP', 'C Reactive Protein', 'C-Reactive Protein'],
  department: 'Immunology',
  sampleType: 'Serum',
  test: {
    code: 'CRP',
    label: 'C-Reactive Protein',
    method: 'Semi-quantitative',
    valueType: 'option',
    options: ['Negative', 'Positive'],
    ranges: [
      {
        type: 'qualitative',
        expected: ['Negative'],
        display: 'Positive: > 6.0 mg/L, Negative: < 6.0 mg/L',
      },
    ],
  },
  rows: [
    section('crp', 'interp', 'Interpretation'),
    narrative(
      'crp',
      'interp',
      'C-reactive protein (CRP) is a normal alpha globulin found in low quantities in serum. It is an acute phase reactant, raised in inflammatory conditions of infectious or non-infectious origin, and is absent in serum from normal individuals. The CRP test is non-specific, but the findings can be used as a simple index of disease activity and treatment status in conditions such as rheumatic fever, rheumatoid arthritis, viral meningitis and bacterial infections. Results of the CRP test frequently overlap with the erythrocyte sedimentation rate (ESR), but CRP is a valuable adjunct and is more sensitive than the ESR.',
    ),
  ],
});

const hbsag = single('hbsag', {
  name: 'HBsAg (Hepatitis B Surface Antigen)',
  aliases: ['HBsAg', 'Hepatitis B', 'Hep B', 'Australia Antigen', 'HBS Ag'],
  department: 'Serology',
  sampleType: 'Serum',
  test: posNeg('HBSAG', 'Hepatitis B Antigen (HBsAg)', 'Rapid immunochromatographic'),
});

const hcv = single('hcv', {
  name: 'HCV (Hepatitis C Virus)',
  aliases: ['HCV', 'Hepatitis C', 'Hep C', 'Anti HCV'],
  department: 'Serology',
  sampleType: 'Serum',
  test: posNeg('HCV', 'Hepatitis C Virus (HCV)', 'Rapid immunochromatographic'),
});

const hav = single('hav', {
  name: 'HAV (Hepatitis A Virus)',
  aliases: ['HAV', 'Hepatitis A', 'Hep A'],
  department: 'Serology',
  sampleType: 'Serum',
  test: posNeg('HAV', 'Hepatitis A Virus (HAV)', 'Rapid immunochromatographic'),
});

const hev = single('hev', {
  name: 'HEV (Hepatitis E Virus)',
  aliases: ['HEV', 'Hepatitis E', 'Hep E'],
  department: 'Serology',
  sampleType: 'Serum',
  test: posNeg('HEV', 'Hepatitis E Virus (HEV)', 'Rapid immunochromatographic'),
});

const P_TYPHI = 'typhi';
const typhidot: TemplateDocInput = {
  name: 'Typhidot (IgM and IgG)',
  aliases: ['Typhidot', 'Typhoid IgM IgG', 'Typhi Dot', 'Salmonella Typhi'],
  department: 'Serology',
  sampleType: 'Serum',
  columns: cols(P_TYPHI),
  rows: [
    test(P_TYPHI, {
      required: true,
      ...posNeg('IGM', 'Typhidot IgM', 'Rapid immunochromatographic'),
    }),
    test(P_TYPHI, {
      required: true,
      ...posNeg('IGG', 'Typhidot IgG', 'Rapid immunochromatographic'),
    }),
  ],
};

const P_ABO = 'abo';
const bloodGroup: TemplateDocInput = {
  name: 'Blood Group (ABO and Rh)',
  aliases: ['Blood Group', 'Blood Grouping', 'ABO', 'ABO Grouping', 'Rh Factor', 'Rh Typing'],
  department: 'Haematology',
  sampleType: 'Whole Blood EDTA',
  columns: cols(P_ABO),
  rows: [
    test(P_ABO, {
      code: 'ABO',
      label: 'ABO Group',
      required: true,
      valueType: 'option',
      options: ['A', 'B', 'AB', 'O'],
      ranges: [{ type: 'text' }],
    }),
    test(P_ABO, {
      code: 'RH',
      label: 'Rh Factor',
      required: true,
      valueType: 'option',
      options: ['Positive', 'Negative'],
      ranges: [{ type: 'text' }],
    }),
  ],
};

export const STARTER_TEMPLATES: Record<string, TemplateDoc> = Object.fromEntries(
  Object.entries({
    cbc,
    lft,
    kft,
    lipid,
    hba1c,
    vitaminD,
    iron,
    thyroid,
    urine,
    semen,
    tshUltra,
    thyroidUltra,
    mpv,
    ptInr,
    aptt,
    calcium,
    phosphorus,
    crp,
    hbsag,
    hcv,
    hav,
    hev,
    typhidot,
    bloodGroup,
  }).map(([k, v]) => [k, TemplateDoc.parse(v)]),
);
